import React from 'react';
import { createSelector } from 'src/universal/redux/utils';

// Selectors
import {
  getPageData,
  getCardsByShortName,
  getPageEep,
  getPageCardsData
} from 'src/universal/redux/page';
import {
  getCardListData,
  getFilteredSelectedCards
} from 'src/universal/redux/compareCardsTray';
import {
  getPznOfferList,
  hasPznDataArrived as getHasPznDataArrived
} from 'src/universal/redux/pznData';
import {
  isGCSMember,
  isLegacyGcsCardMember
} from 'src/universal/redux/cardMemberInfo';
import { getRubiksContentList, getPageAllowedLists } from 'src/universal/redux/page';

// COMPONENTS
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import HowYouEarn from './components/HowYouEarn';
import Tier2Badge from './components/Tier1&2Ribbon.js';
import WhatYouEarn from './components/WhatYouEarn';
import DesignOptions from './components/DesignOptions';
import TrackPznOfferImpression from './components/TrackPznOfferImpression';
import AddToolTip from './components/AddToolTip';
import AttrRowHeader from 'page_modules/compare/components/ProductAttributesTable/AttrRowHeader.js';

// UTILS
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { getUpgradeCtaProps } from 'src/universal/utils/orchestraUtils';
import {
  getFormattedCtaLinks,
  buildModalDataList,
  buildAttrCellDisplay
} from './utils';
import {
  capitalizeButtonText,
  formatSpecialOfferButtonText
} from 'src/universal/utils/formatOfferButton';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import { isCardBlockedFromColorPicker } from 'src/universal/utils/cardColorChoices';

import styles from './__styles__/overviewTable.scss?modules';

const getCardListDataAsObject = createSelector([getCardListData], cardListData =>
  parseArrayToObject(cardListData.filter(card => !!card), 'shortName')
);

// PAGE CONTENT SELECTORS
export const getHeader = createSelector(
  getPageData,
  pageData => pageData?.hero?.header ?? ''
);

export const getSubHeader = createSelector(
  getPageData,
  pageData => pageData?.hero?.description ?? ''
);

export const getCompareSliderDTM = createSelector(getPageData, pageData => {
  const { leftArrowDTM, rightArrowDTM, dotDTM } = parseArrayToObject(
    pageData.dtmEvents,
    'key'
  );
  return { leftArrowDTM, rightArrowDTM, dotDTM };
});

// OFFER BADGE SELECTORS

export const getOfferBadgeText = createSelector(
  [getPznOfferList, (_, shortName) => shortName],
  (pznOfferList, shortName) => {
    const pznOffer = pznOfferList.find(c => c.shortName === shortName) || {};
    const offerButtonText = pznOffer?.offerButton ?? '';
    return capitalizeButtonText(formatSpecialOfferButtonText(offerButtonText));
  }
);

export const getIsTier1Card = createSelector(
  [getPageAllowedLists, (_, shortName) => shortName || ''],
  (allowedlists, shortName) => {
    const isCardInList = (allowedlists.tier1Offers || []).indexOf(shortName) > -1;
    return isCardInList;
  }
);

export const getIsTier2Offer = createSelector(
  [getPznOfferList, (_, shortName) => shortName],
  (pznOfferList, shortName) => {
    const pznOffer = pznOfferList.find(c => c.shortName === shortName) || {};
    const offerButton = pznOffer?.offerButton ?? '';

    const regex = /welcome|extra value/i;
    const offerButtonIncludesSpecial =
      offerButton.includes('special') || offerButton.includes('Special');
    const offerButtonContainsWelcomeOrExtraValue = regex.test(offerButton);
    return (
      offerButton !== '' &&
      (!offerButtonContainsWelcomeOrExtraValue || offerButtonIncludesSpecial)
    );
  }
);

// CTA/TNC Selectors
export const getUpgradeCta = createSelector(
  [
    getCardListDataAsObject,
    getPznOfferList,
    isGCSMember,
    isLegacyGcsCardMember,
    getHasPznDataArrived,
    (_, cardName) => cardName
  ],
  (cardList, pznOfferList, isGcsCm, isLegacyGcsCm, hasPznDataArrived, cardName) => {
    if (!hasPznDataArrived) return {};
    const card = cardList[cardName];
    const pznOffer = pznOfferList.find(({ shortName }) => shortName === cardName);
    const { upgradeBtn, needUpgradeCta, cmPznEventInfo } = getUpgradeCtaProps(
      card,
      pznOffer,
      isGcsCm || isLegacyGcsCm
    );

    return {
      upgradeBtn,
      needUpgradeCta,
      cmPznEventInfo
    };
  }
);

export const makeGetLinks = () =>
  createSelector(
    [
      getCardListDataAsObject,
      getPznOfferList,
      getHasPznDataArrived,
      (_, cardName) => cardName
    ],
    (cardList, pznOfferList, hasPznDataArrived, cardName) => {
      if (!hasPznDataArrived) return {};
      const card = cardList[cardName];
      const offer = pznOfferList.find(offer => offer.shortName === cardName);
      const links = getFormattedCtaLinks({
        originalLinks: card.links,
        pageEep: undefined,
        pznOffer: offer
      });
      return links;
    }
  );

// CARD CONTENT SELECTORS
export const makeGetSelectedCards = () =>
  createSelector(getFilteredSelectedCards, cards => {
    const allThreeCards = [...cards];
    while (allThreeCards.length < 3) {
      allThreeCards.push(null);
    }
    return allThreeCards;
  });

export const makeGetCardData = () =>
  createSelector(
    [
      getCardListDataAsObject,
      getPznOfferList,
      getHasPznDataArrived,
      makeGetLinks(),
      (_, cardName) => cardName
    ],
    (cardList, pznOfferList, hasPznDataArrived, links, cardName) => {
      if (!hasPznDataArrived) return {};
      const card = cardList[cardName];
      const offer = pznOfferList.find(offer => offer.shortName === cardName);
      return {
        ...card,
        pznOffer: offer,
        links
      };
    }
  );

export const getCardImages = createSelector(
  [getCardListDataAsObject, (_, selectedCards) => selectedCards],
  (cardList, selectedCards) =>
    selectedCards.map(shortName => {
      const card = cardList[shortName];

      if (!card) return {};
      return {
        cardArt: card.appAssets.cardArt,
        altText: card.cardArtAltText,
        learnMoreSrc: card?.learnMore?.url,
        shortName: card.shortName
      };
    })
);

export const makeGetCardArt = () =>
  createSelector(
    [getCardListDataAsObject, (_, cardName) => cardName],
    (cardList, cardName) => {
      return cardList[cardName]?.appAssets?.cardArt ?? '';
    }
  );

export const makeGetCardName = () =>
  createSelector(
    [getCardListDataAsObject, (_, cardName) => cardName],
    (cardList, cardName) => {
      return cardList[cardName]?.cardHeadline ?? '';
    }
  );

export const makeGetAnnualFee = () =>
  createSelector(
    [getCardListDataAsObject, (_, cardName) => cardName],
    (cardList, cardName) => {
      return cardList[cardName]?.annualFee ?? '';
    }
  );

// OVERVIEW CONTENT SELECTORS
export const getOverviewKeys = createSelector(
  [getPageData, getCardListData, getRubiksContentList],
  (pageData, cardListData = [], rubiks = []) => {
    /*
    US5763961 (5/8/2025):  We need to filter the attr list to exclude "Design Options"
    if none of the selected cards contains content for it.
    */

    let overviewKeys = Object.keys(pageData.cardOverview.sections);

    const matchedCard = (cardListData || []).find(card => {
      if (!card || isCardBlockedFromColorPicker(card.shortName)) {
        return false;
      }
      const rubiksMatch = rubiks.find(rItem => {
        return (
          rItem.shortName === card.shortName &&
          Array.isArray(rItem.content) &&
          rItem.content.length > 0
        );
      });
      return !!rubiksMatch;
    });

    if (!matchedCard) {
      overviewKeys = overviewKeys.filter(key => key !== 'designOptions');
    }

    return overviewKeys;
  }
);

export const getOverviewContent = createSelector(
  [getPageData],
  pageData => pageData.cardOverview.sections
);

export const getProductOverviewContent = createSelector(
  [getCardListData, getPznOfferList, getRubiksContentList],
  (cardListData, pznOfferList, rubiks) => {
    return cardListData.map(card => {
      if (!card) return null;

      const whatYouCanEarnContent = card?.cardFeatures?.content.find(
        ({ key }) => key === 'whatYouCanEarn'
      );
      const whatYouCanEarn = {
        Component: <WhatYouEarn content={whatYouCanEarnContent} styles={styles} />
      };

      const aprText =
        pznOfferList.find(({ shortName }) => shortName === card?.shortName)?.rate ||
        ' ';
      const apr = {
        Component: <Sanitizer safelySetInnerHTML={aprText} />
      };

      const howToEarnContent = card?.cardFeatures?.content.find(
        ({ key }) => key === 'howToEarn'
      )?.content;
      const howToEarn = {
        Component: <HowYouEarn content={howToEarnContent} />
      };

      const offerObj = pznOfferList.find(
        ({ shortName }) => shortName === card?.shortName
      );
      const offerText = offerObj?.header || '';
      const offerDesc = offerObj?.description || null;
      const welcomeBonus = {
        Component: (
          <React.Fragment>
            <TrackPznOfferImpression shortName={card?.shortName} />
            <Tier2Badge
              shortName={card.shortName}
              styles={styles}
              defaultOfferText={offerText}
              isHeader={false}
            />
            <Sanitizer
              Component={offerText ? 'h2' : 'span'}
              className={styles.welcomeOfferHeader}
              safelySetInnerHTML={offerText}
            />
            {offerDesc && (
              <Sanitizer
                className={styles.welcomeOfferDesc}
                safelySetInnerHTML={offerDesc}
              />
            )}
          </React.Fragment>
        )
      };

      const annualFeeText = card?.annualFee || '';

      const annualFee = {
        Component: <Sanitizer Component="b" safelySetInnerHTML={annualFeeText} />
      };

      const rubikContent =
        rubiks.find(eachRubik => eachRubik.shortName === card.shortName)?.content ||
        [];

      const designOptions = {
        Component: (
          <DesignOptions
            shortName={card.shortName}
            content={rubikContent}
            styles={styles}
          />
        )
      };

      return {
        welcomeBonus,
        annualFee,
        apr,
        designOptions,
        whatYouCanEarn,
        howToEarn
      };
    });
  }
);

// HIGHLIGHT CONTENT SELECTORS
const getAllPossibleHighlights = createSelector([getPageData], pageData =>
  Object.keys(pageData.highlights.sections)
);

const getHighlightsForAllSelectedCards = createSelector([getCardListData], cards => {
  const highlightsForAllSelectedCards = cards
    .filter(card => card)
    .reduce((acc, { cardHighlights }) => {
      cardHighlights.forEach(highlight => {
        if (!acc[highlight.key]) {
          acc[highlight.key] = highlight;
        }
      });
      return acc;
    }, {});
  return highlightsForAllSelectedCards;
});

export const getSelectedCardsHighlights = createSelector(
  [getHighlightsForAllSelectedCards, getAllPossibleHighlights],
  (highlightsForAllSelectedCards, allHighlights) => {
    return allHighlights.filter(
      highlightName => highlightsForAllSelectedCards[highlightName]
    );
  }
);

export const getAttributeContent = createSelector([getPageData], pageData => {
  const sectionsHash = pageData?.highlights?.sections || {};
  return (Object.keys(sectionsHash) || []).reduce((accum, key) => {
    const valObj = sectionsHash[key] || {};
    const text = Array.isArray(valObj.desc) ? valObj.desc[0]?.text : null;
    const toolTipEle =
      typeof text === 'string' && text.length > 0 ? (
        <AddToolTip text={text} benefitName={valObj.header} />
      ) : (
        undefined
      );
    accum[key] = {
      ...valObj,
      header: <AttrRowHeader header={valObj.header} tag={valObj.tag} />,
      Tooltip: toolTipEle
    };
    return accum;
  }, {});
});

export const getAttributeTableData = createSelector([getPageData], pageData => {
  return pageData?.highlights || {};
});

export const getCardAttributeContent = createSelector(
  [getSelectedCardsHighlights, getCardListData],
  (attrList, cardListData) => {
    return cardListData.map(card => {
      const cardAttrList = Array.isArray(card?.cardHighlights)
        ? card.cardHighlights
        : [];
      return attrList.reduce((accum, attrName) => {
        const matchedItem = cardAttrList.find(item => item.key === attrName);
        accum[attrName] = buildAttrCellDisplay(matchedItem, attrName);
        return accum;
      }, {});
    });
  }
);

// OPEN FAQ SELECTORS
export const getOpenFAQ = createSelector([getPageData], pageData => {
  return pageData?.openFAQ;
});

export const getApplyModalData = createSelector(
  [getCardListData, getPageEep()],
  (cardList, pgEep) => {
    return buildModalDataList(cardList, pgEep);
  }
);

// cardOverview data
export const getCardOverviewData = createSelector([getPageData], pageData => {
  return pageData?.cardOverview;
});

// card list for card-selector/card-picker
export const getCardSelectorCards = createSelector(
  [
    getPageData,
    getCardsByShortName,
    getFilteredSelectedCards,
    getPageCardsData,
    getPznOfferList,
    getHasPznDataArrived
  ],
  (pageData, cards, selectedCards, cardList, offerList, pznArrived) => {
    return pageData.compareCardList.content.map(category => {
      let filteredCatCards = category.cards;
      if (pznArrived && Array.isArray(offerList)) {
        filteredCatCards = filteredCatCards.filter(cardName => {
          return !!offerList.find(offer => offer.shortName === cardName);
        });
      }

      return {
        ...category,
        cards: filteredCatCards.map(shortName => {
          const card = cards[shortName];
          const plainName = textRemoveTags(card.cardHeadline);
          const targetCard = cardList.find(card => card.shortName === shortName);
          const dtmProductName = targetCard?.dtmProductName || shortName;

          return {
            imgSrc: card.appAssets.cardArt,
            imgAlt: '',
            cardName: card.cardHeadline,
            shortName,
            isSelected: selectedCards.includes(shortName),
            annualFee: card.annualFee,
            ariaLabelAddCard: `${plainName}, Deselected`,
            ariaLabelRemoveCard: `${plainName}`,
            dtmProductName
          };
        })
      };
    });
  }
);

export const getAddSupCardData = createSelector(
  getPageData,
  pageData => (pageData || {}).addSupUser
);

export const makeGetAsortedCardData = () =>
  createSelector([makeGetCardData(), (_, cardName) => cardName], cardData => {
    const linkObj = cardData.links?.cardArt;
    const imgAlt = cardData.cardArtAltText || '';
    const ariaLabel = cardData.cardArtAltText;
    const defaultOfferButtonText = cardData.defaultOfferButtonText;
    const dtm = {
      ...cardData.learnMore?.dtmTag,
      tag: 'CardArt'
    };
    return { linkUrl: linkObj?.url, imgAlt, ariaLabel, dtm, defaultOfferButtonText };
  });
